/**
 * Normalize messy user prompts into clear scene-editor commands
 * BEFORE interpretation / enrichment / tool execution.
 *
 * Pipeline: raw → localNormalize → (Bedrock rewrite) → interpret → enrich → execute
 */

const TYPO_MAP = {
  // shapes
  cub: 'cube',
  cubes: 'cubes',
  shpere: 'sphere',
  spehre: 'sphere',
  sylinder: 'cylinder',
  cylindr: 'cylinder',
  cylnder: 'cylinder',
  cilynder: 'cylinder',
  conos: 'cones',
  pyrimid: 'pyramid',
  piramid: 'pyramid',
  capasule: 'capsule',
  capsul: 'capsule',
  toris: 'torus',
  torous: 'torus',
  toreus: 'torus',
  rectange: 'rectangle',
  rectangl: 'rectangle',
  elipse: 'ellipse',
  triange: 'triangle',
  // effects
  rian: 'rain',
  snoow: 'snow',
  firre: 'fire',
  // verbs / grammar-ish
  ad: 'add',
  creat: 'create',
  cretae: 'create',
  creatre: 'create',
  delte: 'delete',
  remvoe: 'remove',
  remoe: 'remove',
  mak: 'make',
  maek: 'make',
  chnage: 'change',
  chang: 'change',
  colur: 'color',
  colour: 'color',
  coloer: 'color',
  oragne: 'orange',
  ornage: 'orange',
  perpel: 'purple',
  purpel: 'purple',
  yello: 'yellow',
  yelow: 'yellow',
  // rotation / axes
  rotatd: 'rotated',
  rotaed: 'rotated',
  roteted: 'rotated',
  rotaton: 'rotation',
  degre: 'degree',
  degres: 'degrees',
  degress: 'degrees',
  axsis: 'axis',
  axs: 'axis',
  // layout
  verticle: 'vertical',
  vertiacal: 'vertical',
  horizantal: 'horizontal',
  horizonal: 'horizontal',
  stright: 'straight',
  staight: 'straight',
  streight: 'straight',
  // misc
  hotsport: 'hotspot',
  hotpsot: 'hotspot',
  sketcfab: 'sketchfab',
  sketchfab: 'sketchfab',
};

const PHRASE_REPLACEMENTS = [
  [/\bgimme\b/gi, 'add'],
  [/\bgive\s+me\b/gi, 'add'],
  [/\bcan\s+you\s+(?:please\s+)?/gi, ''],
  [/\bcould\s+you\s+(?:please\s+)?/gi, ''],
  [/\bplease\b/gi, ''],
  [/\bpls\b/gi, ''],
  [/\bplz\b/gi, ''],
  [/\bkindly\b/gi, ''],
  [/\bi\s+want\s+(?:you\s+to\s+)?/gi, ''],
  [/\bi\s+need\s+(?:you\s+to\s+)?/gi, ''],
  [/\bwanna\b/gi, 'want to'],
  [/\bgonna\b/gi, 'going to'],
  [/\bput\s+(?:a|an|the|some)?\s*/gi, 'add '],
  [/\bplace\s+(?:a|an|the|some)?\s*/gi, 'add '],
  [/\bdrop\s+(?:a|an|the|some)?\s*/gi, 'add '],
  [/\bspawn\s+(?:a|an|the|some)?\s*/gi, 'add '],
  [/\binsert\s+(?:a|an|the|some)?\s*/gi, 'add '],
  [/\bmake\s+me\s+(?:a|an|some)?\s*/gi, 'add '],
  [/\bcreate\s+me\s+(?:a|an|some)?\s*/gi, 'add '],
  // degree variants → °
  [/\b(\d+(?:\.\d+)?)\s*(?:degrees?|degs?|deg)\b/gi, '$1°'],
  [/\b(\d+(?:\.\d+)?)\s*degree\b/gi, '$1°'],
  // axis shorthand (only when axis word present or bare letter at end)
  [/\bon\s+([xyz])(?:\s*axis|\s*axes)?\b/gi, 'on $1 axis'],
  [/\baround\s+([xyz])(?:\s*axis|\s*axes)?\b/gi, 'around $1 axis'],
  [/\balong\s+([xyz])(?:\s*axis|\s*axes)?\b/gi, 'along $1 axis'],
  [/\bontop\b/gi, 'on top'],
  [/\bon\s*-\s*top\b/gi, 'on top'],
  // "in a line" helpers
  [/\bin\s+(?:a\s+)?straight\s+line\s+vertical\b/gi, 'in a straight line vertically'],
  [/\bstraight\s+line\s+vertical\b/gi, 'in a straight line vertically'],
  [/\bcylinder\s+straight\s+line\b/gi, 'cylinders in a straight line'],
  [/\b(\d+)\s+(\w+)\s+straight\s+line\b/gi, '$1 $2 in a straight line'],
  // "all of the cubes" → "all cubes"
  [/\ball\s+of\s+the\b/gi, 'all'],
  [/\bevery\s+single\b/gi, 'every'],
  // vertical line variants
  [/\bin\s+(?:a\s+)?(?:row|line)\s+vertical(?:ly)?\b/gi, 'in a straight line vertically'],
  [/\bvertical(?:ly)?\s+(?:in\s+)?(?:a\s+)?(?:straight\s+)?line\b/gi, 'in a straight line vertically'],
  [/\bin\s+(?:a\s+)?vertical\s+(?:straight\s+)?line\b/gi, 'in a straight line vertically'],
];

/**
 * Fast deterministic cleanup — always runs before AI / fallback.
 * Does not invent intent; only cleans spelling, slang, and formatting.
 */
export function localNormalizeCommand(raw) {
  let text = String(raw || '').trim();
  if (!text) return '';

  // Normalize quotes / whitespace / punctuation noise
  text = text
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Strip trailing filler
  text = text.replace(/[?.!]+$/g, '').trim();

  // Token-level typo fixes first (so phrase rules see corrected words)
  text = text
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token)) return token;
      const punct = token.match(/^([^\w]*)(.*?)([^\w]*)$/);
      if (!punct) return token;
      const [, lead, core, trail] = punct;
      if (!core) return token;
      const lower = core.toLowerCase();
      const fixed = TYPO_MAP[lower];
      if (!fixed) return token;
      if (
        core[0] === core[0].toUpperCase() &&
        core.slice(1) === core.slice(1).toLowerCase()
      ) {
        return lead + fixed.charAt(0).toUpperCase() + fixed.slice(1) + trail;
      }
      return lead + fixed + trail;
    })
    .join('');

  // Phrase-level fixes
  for (const [pattern, replacement] of PHRASE_REPLACEMENTS) {
    text = text.replace(pattern, replacement);
  }

  // Collapse duplicate "add add"
  text = text.replace(/\b(add|create|make)\s+\1\b/gi, '$1');
  text = text.replace(/\s+/g, ' ').trim();

  // Prefer imperative starting with a verb when user omitted it but meant create
  // e.g. "3 red cubes in a line" → "add 3 red cubes in a line"
  if (
    /^(?:\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten)\b/i.test(
      text
    ) &&
    /\b(cube|sphere|cylinder|cone|pyramid|capsule|torus|rectangle|ellipse|triangle|plane|rain|snow|fire)s?\b/i.test(
      text
    )
  ) {
    text = `add ${text}`;
  }

  // "all cube" / "every sphere" → plural when no quantity word
  text = text.replace(
    /\b(all|every|each)\s+(cube|sphere|cylinder|cone|pyramid|capsule|torus|rectangle|ellipse|triangle|plane)(?!s)\b/gi,
    '$1 $2s'
  );

  // "5 blue cylinder" → "5 blue cylinders"
  text = text.replace(
    /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+((?:(?:dark|light|bright|dim)\s+)?(?:red|blue|green|orange|yellow|purple|pink|white|black|gray|grey)\s+)?(cube|sphere|cylinder|cone|pyramid|capsule|torus|rectangle|ellipse|triangle|plane)(?!s)\b/gi,
    '$1 $2$3s'
  );

  // "on top cube" / "on top of cube" → "on top of a cube"
  text = text.replace(
    /\bon\s+top(?:\s+of)?\s+(?:a|an|the)?\s*(cube|sphere|cylinder|cone|pyramid|capsule|torus)\b/gi,
    'on top of a $1'
  );

  // Bare shape + rotation without verb
  if (
    /^(cube|sphere|cylinder|cone|pyramid|capsule|torus|rectangle|ellipse|triangle|plane)s?\b/i.test(
      text
    )
  ) {
    text = `add a ${text.replace(/^(a|an)\s+/i, '')}`;
  }

  return text;
}

/**
 * Pick the best command string to drive interpret + enrich.
 * Prefer AI-rewritten normalizedCommand when present and non-empty.
 */
export function resolveWorkingCommand(original, aiNormalized) {
  const local = localNormalizeCommand(original);
  const fromAi = String(aiNormalized || '').trim();
  if (fromAi && fromAi.length >= 2) {
    // Light local pass on AI rewrite too (typos AI may leave)
    return localNormalizeCommand(fromAi) || fromAi;
  }
  return local || String(original || '').trim();
}

/**
 * Result shape used across the automation pipeline.
 */
export function buildNormalizedPrompt(original, aiNormalized = null) {
  const originalTrimmed = String(original || '').trim();
  const local = localNormalizeCommand(originalTrimmed);
  const working = resolveWorkingCommand(originalTrimmed, aiNormalized);
  return {
    original: originalTrimmed,
    local,
    normalized: working,
    changed: working.toLowerCase() !== originalTrimmed.toLowerCase(),
  };
}
